import React from "react";
import { postRequest } from "../functions/api_functions";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, PieChart, Pie, Cell, ResponsiveContainer
} from "recharts";

// Tipos

export type DashboardItem = {
  school_cnpj: string;
  school_name: string;
  school_city: string;
  school_unit: string;
  school_neighborhood: string;
  type_description: string;
  students_re: number;
  students_birthdate: string;
  students_gender: string;
  sentences_id: number;
  alternative_id: number;
  response_date: string;
  students_serie: string;
  students_class: string;
  students_shift: string;
  student_age: number;
  subscale: string;
  perception: string;
  total_score: number;
};

export type DashboardResponse = {
  status_code: number;
  values: DashboardItem[];
};

type FilterKeys = keyof Pick<DashboardItem,
  "school_name" | "type_description" | "school_unit" | "school_city" | "school_neighborhood" |
  "students_serie" | "students_class" | "students_gender" | "student_age" |
  "students_re" | "perception" | "subscale" | "total_score">

const filterLabels: Record<FilterKeys, string> = {
  school_name: "Nome da Escola",
  type_description: "Tipo de Escola",
  school_unit: "Estado",
  school_city: "Cidade",
  school_neighborhood: "Bairro",
  students_serie: "Série",
  students_class: "Turma",
  students_gender: "Gênero",
  student_age: "Idade",
  students_re: "RA",
  perception: "Percepção",
  subscale: "Subescala",
  total_score: "Nota",
};

const Dashboard: React.FC = () => {
  const [data, setData] = React.useState<DashboardItem[]>([]);
  const [filteredData, setFilteredData] = React.useState<DashboardItem[]>([]);
  const [uniqueValues, setUniqueValues] = React.useState<Partial<Record<FilterKeys, (string | number)[]>>>({});
  const [filters, setFilters] = React.useState<Record<FilterKeys, (string | number)[]>>({
    school_name: ["Todos"],
    type_description: ["Todos"],
    school_unit: ["Todos"],
    school_city: ["Todos"],
    school_neighborhood: ["Todos"],
    students_serie: ["Todos"],
    students_class: ["Todos"],
    students_gender: ["Todos"],
    student_age: ["Todos"],
    students_re: ["Todos"],
    perception: ["Todos"],
    subscale: ["Todos"],
    total_score: ["Todos"],
  });

  React.useEffect(() => {
    const loadData = async () => {
      try {
        const response = await postRequest<DashboardResponse>("/dashboard_data/", {
          body: { school_cnpj: null },
          headers: {},
        });

        if (response.status_code === 200) {
          setData(response.values);
          setUniqueValues(extractUniqueValues(response.values));
        }
      } catch (err) {
        console.error("Erro ao carregar dados");
      }
    };

    loadData();
  }, []);

  const extractUniqueValues = (items: DashboardItem[]) => {
    const keys: FilterKeys[] = Object.keys(filters) as FilterKeys[];
    const result: Partial<Record<FilterKeys, (string | number)[]>> = {};
    keys.forEach(key => {
      result[key] = ["Todos", ...Array.from(new Set(items.map(item => item[key])))];
    });
    return result;
  };

  const handleCheckboxChange = (key: FilterKeys, value: string | number) => {
    setFilters(prev => {
      const current = new Set(prev[key]);
      if (value === "Todos") {
        return { ...prev, [key]: ["Todos"] };
      }
      if (current.has(value)) {
        current.delete(value);
      } else {
        current.add(value);
        current.delete("Todos");
      }
      const updated = Array.from(current);
      return { ...prev, [key]: updated.length ? updated : ["Todos"] };
    });
  };

  React.useEffect(() => {
    const isAll = (key: FilterKeys, value: string | number) =>
      filters[key].includes("Todos") || filters[key].includes(value);

    const filtered = data.filter(item =>
      isAll("school_name", item.school_name) &&
      isAll("type_description", item.type_description) &&
      isAll("school_unit", item.school_unit) &&
      isAll("school_city", item.school_city) &&
      isAll("school_neighborhood", item.school_neighborhood) &&
      isAll("students_serie", item.students_serie) &&
      isAll("students_class", item.students_class) &&
      isAll("students_gender", item.students_gender) &&
      isAll("student_age", item.student_age) &&
      isAll("students_re", item.students_re) &&
      isAll("perception", item.perception) &&
      isAll("subscale", item.subscale) &&
      isAll("total_score", item.total_score)
    );

    setFilteredData(filtered);
  }, [filters, data]);

  // Big Numbers
  const totalRespostas = filteredData.length;
  const mediaPontuacao = (filteredData.reduce((acc, cur) => acc + cur.total_score, 0) / (totalRespostas || 1)).toFixed(2);
  const idadeMedia = (filteredData.reduce((acc, cur) => acc + cur.student_age, 0) / (totalRespostas || 1)).toFixed(1);
  const escolasUnicas = new Set(filteredData.map(d => d.school_name)).size;
  const seriesUnicas = new Set(filteredData.map(d => d.students_serie)).size;

  // Gráficos
  const groupCount = (key: keyof DashboardItem) => {
    const map = new Map<string, number>();
    filteredData.forEach(item => {
      const k = item[key]?.toString() || "";
      map.set(k, (map.get(k) || 0) + 1);
    });
    return Array.from(map, ([name, value]) => ({ name, value }));
  };

  const groupAverage = (key: keyof DashboardItem) => {
    const map = new Map<string, { total: number, count: number }>();
    filteredData.forEach(item => {
      const k = item[key]?.toString() || "";
      const obj = map.get(k) || { total: 0, count: 0 };
      obj.total += item.total_score;
      obj.count += 1;
      map.set(k, obj);
    });
    return Array.from(map, ([name, { total, count }]) => ({ name, value: +(total / count).toFixed(2) }));
  };

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#A28BE1", "#E16262"];

  return (
    <div style={{ padding: "20px" }}>
      <h2>Dashboard</h2>

      {/* Big Numbers */}
      <div style={{ display: "flex", gap: 20, marginBottom: 20 }}>
        <div><strong>Total de Respostas:</strong> {totalRespostas}</div>
        <div><strong>Média de Pontuação:</strong> {mediaPontuacao}</div>
        <div><strong>Idade Média:</strong> {idadeMedia}</div>
        <div><strong>Escolas:</strong> {escolasUnicas}</div>
        <div><strong>Séries:</strong> {seriesUnicas}</div>
      </div>

      {/* Filtros */}
      <div style={{ display: "flex" }}>
        <div style={{ width: 250, marginRight: 20 }}>
          <h4>Filtros</h4>
          {Object.keys(uniqueValues).map((key) => {
            const typedKey = key as FilterKeys;
            const values = uniqueValues[typedKey];
            if (!values) return null;
            const isScrollable = typedKey === "students_re";
            return (
              <div key={key} style={{ marginBottom: 12 }}>
                <strong>{filterLabels[typedKey]}</strong>
                <div style={isScrollable ? { maxHeight: "200px", overflowY: "auto", border: "1px solid #ddd", padding: "4px" } : {}}>
                  {values.map((val) => (
                    <div key={val.toString()}>
                      <label>
                        <input
                          type="checkbox"
                          checked={filters[typedKey].includes(val)}
                          onChange={() => handleCheckboxChange(typedKey, val)}
                        /> {val}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Gráficos */}
        <div style={{ flex: 1 }}>
          <h4>Gráfico: Respostas por Percepção</h4>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={groupCount("perception")}>  
              <XAxis dataKey="name" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend payload={[{ value: 'Quantidade de Respostas', type: 'square', color: '#8884d8' }]} />
              <Bar dataKey="value" fill="#8884d8" name="Quantidade de Respostas" />
            </BarChart>
          </ResponsiveContainer>

          <h4>Gráfico: Média de Pontuação por Subescala</h4>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={groupAverage("subscale")}>  
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend payload={[{ value: 'Média de Pontuação', type: 'square', color: '#82ca9d' }]} />
              <Bar dataKey="value" fill="#82ca9d" name="Média de Pontuação" />
            </BarChart>
          </ResponsiveContainer>

          <h4>Gráfico: Distribuição por Gênero</h4>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={groupCount("students_gender")}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label
              >
                {groupCount("students_gender").map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Legend />
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
